import IconLocationPinMap from '@/components/icon/IconLocationPinMap';
import { DEFAULT_COORDINATES, ekDomain, ROUTES } from '@/configs/constants';
import { URL_CONFIG } from '@/configs/environment';
import { useStore } from '@/hooks/useStore';
import { COMMON_TYPE, LANGUAGES_SUPPORTED } from '@/types/enums';
import { LocationBike } from '@/types/http-payload/locationBike';
import { Coordinate } from '@/types/location';
import { isPathEk } from '@/utils/routes';
import {
    AdvancedMarker,
    APIProvider,
    InfoWindow,
    Map,
    useMap
} from '@vis.gl/react-google-maps';
import { MapPinIcon } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

interface CurrentMapLocationProps {
    latitudeSearch: number | string | null | undefined,
    longitudeSearch: number | string | null | undefined
}
export default observer(function CurrentMapLocation({ latitudeSearch, longitudeSearch }: CurrentMapLocationProps) {
    // hook
    const { t, i18n: { language } } = useTranslation();
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    const [selectedLocation, setSelectedLocation] = useState<any>(null);

    // store
    const {
        locationBikeStore: {
            bikePaging,
            getAllBikeInLocation,
            setObservable
        },
        locationEnduserStore: {
            locationsCurrent,
            getLocationDetail
        }
    } = useStore();

    const currentCoordinates = useMemo((): Coordinate => {
        if (latitudeSearch && longitudeSearch) {
            return {
                lat: Number(latitudeSearch),
                lng: Number(longitudeSearch)
            };
        }
        return DEFAULT_COORDINATES;
    }, [latitudeSearch, longitudeSearch]);

    const locationsPinMap = useMemo((): LocationBike[] => {
        const locations = locationsCurrent?.filter(item => item.latitude && item.longitude);
        return (locations && locations?.length > 0) ? locations : [];
    }, [locationsCurrent]);

    // lifeCycle
    useEffect(() => {
        setSelectedLocation(null);
    }, [locationsCurrent]);

    // function
    const handleMarkerClick = (location) => {
        setSelectedLocation(location);
        fetchData(location);
    };

    const handleInfoWindowClose = () => {
        setSelectedLocation(null);
    };

    const fetchData = async (location) => {
        setObservable('keepSearchParams', undefined);

        if (!location) {
            return;
        }
        const { id } = location;
        const res = await flowResult(
            getLocationDetail({
                id,
                type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS
            })
        );

        if (res) {
            await flowResult(
                getAllBikeInLocation(
                    {
                        id: res.data.id,
                        type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS
                    },
                    {
                        ...bikePaging,
                        pagination: { ...bikePaging.pagination, pageIndex: 0 }
                    }
                )
            );
        }
    };

    return (
        <APIProvider
            apiKey={`${URL_CONFIG.GOOGLE_MAP.private}`}
            language={language === LANGUAGES_SUPPORTED.Japanese ? 'ja' : 'en'}
            region={language === LANGUAGES_SUPPORTED.Japanese ? 'JP' : 'US'}
        >
            <Map
                id='location-current'
                mapId='location-current'
                style={{ height: '100%', width: '100%' }}
                defaultCenter={currentCoordinates}
                defaultZoom={16}
                minZoom={3}
                gestureHandling='cooperative'
                streetViewControl={false}
                mapTypeControl={false}
                fullscreenControl={false}
                zoomControl={true}
            >
                <MapContent
                    currentCoordinates={currentCoordinates}
                    locationsCurrent={locationsPinMap}
                />
                {locationsPinMap?.length > 0 && (
                    <>
                        {locationsPinMap?.map(location => (
                            <div key={location?.id}>
                                <AdvancedMarker
                                    position={{
                                        lat: +location.latitude,
                                        lng: +location.longitude
                                    }}
                                    onClick={() => handleMarkerClick(location)}
                                    zIndex={selectedLocation?.id === location.id ? 1000 : 1}
                                >
                                    <IconLocationPinMap />
                                </AdvancedMarker>
                            </div>
                        ))}
                        {selectedLocation && (
                            <InfoWindow
                                pixelOffset={[0, -55]}
                                className='w-auto pb-3 md:pb-0'
                                position={{
                                    lat: +selectedLocation.latitude,
                                    lng: +selectedLocation.longitude
                                }}
                                onCloseClick={handleInfoWindowClose}
                                headerContent={(
                                    <Link
                                        target='_blank'
                                        to={
                                            isEk ?
                                                `/${ekDomain}/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${selectedLocation?.id}` :
                                                `/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${selectedLocation?.id}`
                                        }
                                        className='font-medium text-[14px] link-1'
                                    >
                                        {selectedLocation.name}
                                    </Link>
                                )}
                            >
                                <div>
                                    <div className='text-[13px] mb-2 flex items-center space-x-0.5'>
                                        <div className='w-4'>
                                            <MapPinIcon className='w-4 h-4' />
                                        </div>
                                        <div className='flex-1 break-all'>
                                            {selectedLocation?.address || t('words_title.no_address')}
                                        </div>
                                    </div>
                                    <a
                                        className='text-[#1A73E8]'
                                        href={`https://www.google.com/maps?q=${selectedLocation.latitude},${selectedLocation.longitude}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                    >
                                        {t('words_title.view_on_ggmap')}
                                    </a>
                                </div>
                            </InfoWindow>
                        )}
                    </>
                )}

                {currentCoordinates && (
                    <AdvancedMarker
                        position={{
                            lat: currentCoordinates.lat,
                            lng: currentCoordinates.lng
                        }}
                        zIndex={999}
                    />
                )}
            </Map>
        </APIProvider>
    );
});

const MapContent = observer(({ currentCoordinates, locationsCurrent }: any) => {
    const mapRef = useMap('location-current');

    useEffect(() => {
        if (window.google && mapRef) {
            if (locationsCurrent && locationsCurrent.length > 0) {
                const bounds = new google.maps.LatLngBounds();

                if (currentCoordinates) {
                    bounds.extend(currentCoordinates);
                }

                locationsCurrent.forEach((location) => {
                    if (location.latitude && location.longitude) {
                        bounds.extend({
                            lat: +location.latitude,
                            lng: +location.longitude
                        });
                    }
                });

                google.maps.event.addListenerOnce(mapRef, 'bounds_changed', () => {
                    const currentZoom = mapRef ? mapRef.getZoom() : null;
                    if (currentZoom && currentZoom > 19) {
                        if (mapRef) {
                            mapRef.setZoom(16);
                        }
                    }
                });

                mapRef.fitBounds(bounds);
                const center = bounds.getCenter();
                mapRef.setCenter(center);
            } else {
                mapRef.setCenter(currentCoordinates);
                mapRef.setZoom(16);
            }

            mapRef.setOptions({
                restriction: {
                    latLngBounds: {
                        north: 85,
                        south: -85,
                        west: -180,
                        east: 180
                    },
                    strictBounds: true
                }
            });
        }
    }, [mapRef, currentCoordinates?.lat, locationsCurrent?.length]);

    return null;
});
